import React, { useState, useEffect, useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import { getProjectById, getCategories, createCategory, getTasks, createTask, updateCategory, deleteCategory, reorderTasks, updateTaskCategory, getProjectInstructions, createProjectInstruction, getAllTaskCommentsByProject, getComments, getFeedbacks, updateProjectVisit } from '../../../api/pmsApi';
import TaskCreateModal from './TaskCreateModal';
import TaskDrawerPanel from './TaskDrawerPanel';
import HomeTab from './tabs/HomeTab';
import TimelineTab from './tabs/TimelineTab';
// eslint-disable-next-line no-unused-vars
import BoardTab from './tabs/BoardTab';
import FilesTab from './tabs/FilesTab';
import CommentsTab from './tabs/CommentsTab';
// eslint-disable-next-line no-unused-vars
// import SettingsTab from './tabs/SettingsTab';
import BusinessOrderTab from './tabs/BusinessOrderTab';
import ProjectView from '../ProjectView';
import ProjectForm from '../ProjectForm';

// Gantt
// eslint-disable-next-line no-unused-vars
import { Gantt, ViewMode } from 'gantt-task-react';
import "gantt-task-react/dist/index.css";

// FullCalendar
// eslint-disable-next-line no-unused-vars
import FullCalendar from '@fullcalendar/react';
// eslint-disable-next-line no-unused-vars
import dayGridPlugin from '@fullcalendar/daygrid';
// eslint-disable-next-line no-unused-vars
import interactionPlugin from '@fullcalendar/interaction';

function ProjectWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // eslint-disable-next-line no-unused-vars
  const { token, user } = useContext(AuthContext);

  const [project, setProject] = useState(null);
  const [categories, setCategories] = useState([]);
  const [tasks, setTasks] = useState([]);

  // 사용자 권한 확인용 (마스터 여부)
  const isProjectMaster = React.useMemo(() => {
    if (!user || !project) return false;
    if (user.role === 'admin') return true;
    const uId = String(user.id);
    const uLoginId = user.userid ? String(user.userid) : uId;
    return String(project.author_id) === uId || String(project.author_id) === uLoginId ||
      String(project.leader_id) === uId || String(project.leader_id) === uLoginId ||
      String(project.reviewer_id) === uId || String(project.reviewer_id) === uLoginId;
  }, [user, project]);

  // 프로젝트 일반 담당자 여부 (참조자 제외)
  const isProjectMember = React.useMemo(() => {
    if (!user) return false;
    const uId = String(user.id);
    const uLoginId = user.userid ? String(user.userid) : uId;

    // 1순위: 프로젝트 전체 멤버인지 확인 (프로젝트 멤버는 모두 담당자 권한 가짐)
    let isManagerInProject = false;
    if (project && project.members) {
      isManagerInProject = project.members.some(m =>
        String(m.user_id) === uId ||
        String(m.user_id) === uLoginId ||
        (m.user && String(m.user.id) === uId) ||
        (m.user && String(m.user.userid) === uLoginId)
      );
    }

    // 2순위: 프로젝트 멤버에 없더라도, 세부업무(Task) 중 하나라도 담당자로 지정되어 있는지 확인
    let isManagerInAnyTask = false;
    if (tasks && tasks.length > 0) {
      isManagerInAnyTask = tasks.some(t => {
        if (!t.members) return false;
        return t.members.some(m =>
          (m.is_manager === true || m.is_manager === 1) &&
          (String(m.user_id) === uId ||
            String(m.user_id) === uLoginId ||
            (m.user && String(m.user.id) === uId) ||
            (m.user && String(m.user.userid) === uLoginId))
        );
      });
    }

    return isManagerInProject || isManagerInAnyTask;
  }, [user, project, tasks]);

  // 세부업무 추가 가능 권한 (마스터 + 일반 담당자)
  const canCreateTask = isProjectMaster || isProjectMember;

  // Top Tabs: 'home', 'timeline', 'calendar', 'files', 'comments', 'settings'
  const [activeTab, setActiveTab] = useState('home');
  const [isEditingSettings, setIsEditingSettings] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [drawerTab, setDrawerTab] = useState('checklist');
  const [drawerFocusId, setDrawerFocusId] = useState(null);
  const [collapsedPhases, setCollapsedPhases] = useState({});
  const [editCategoryModal, setEditCategoryModal] = useState({ isOpen: false, categoryId: null, name: '', sortOrder: 10 });
  const [addCategoryModal, setAddCategoryModal] = useState({ isOpen: false, name: '', sortOrder: 10 });

  // HOME 탭 칸반 섹션 전체 펼치기/접기
  const expandAllPhases = () => setCollapsedPhases({});
  const collapseAllPhases = () => {
    const allCollapsed = {};
    categories.forEach(cat => { allCollapsed[cat.id] = true; });
    setCollapsedPhases(allCollapsed);
  };

  // 달력 뷰(일정) 멤버 필터링 상태 (하드코딩 for demo)
  // eslint-disable-next-line no-unused-vars
  const [calendarMembers, setCalendarMembers] = useState([
    { id: 1, name: '과영래', color: '#0d6efd', checked: true },
    { id: 2, name: '박학송', color: '#fd7e14', checked: true },
    { id: 3, name: '안성래', color: '#20c997', checked: true },
  ]);

  // 코멘트 & 지시사항 전용 
  const [allTaskComments, setAllTaskComments] = useState([]);
  const [instructions, setInstructions] = useState([]);
  const [newInstruction, setNewInstruction] = useState('');
  const [refreshTaskSignal, setRefreshTaskSignal] = useState(null);

  const refreshTaskData = async (taskId) => {
    try {
      const res = await getComments(taskId, token);
      if (res.ok) {
        setAllTaskComments(prev => {
          const filtered = (prev || []).filter(c => String(c.task_id) !== String(taskId));
          return [...filtered, ...res.data];
        });
      }
      setRefreshTaskSignal({ taskId, time: Date.now() });
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreateInstruction = async () => {
    if (!newInstruction.trim()) return;
    try {
      const res = await createProjectInstruction(id, newInstruction, token);
      if (res.ok) {
        const inst = res.data;
        const dept = inst.user?.org?.dept_name ? ` [${inst.user.org.dept_name}]` : '';
        const formatted = {
          id: inst.id,
          author: `${inst.user?.name || 'Unknown'}${dept}`,
          content: inst.content,
          date: new Date(inst.created_at).toLocaleString()
        };
        setInstructions([...instructions, formatted]);
        setNewInstruction('');
      }
    } catch (err) {
      console.error(err);
      alert('작업요청 등록에 실패했습니다.');
    }
  };

  // 프로젝트 설정 탭 섹션 상태 (false = 펼쳐짐, true = 접힘)
  /* eslint-disable no-unused-vars */
  const [settingsCollapsed, setSettingsCollapsed] = useState({ basic: false, people: false, detail: false });
  const [unresolvedFeedCount, setUnresolvedFeedCount] = useState(0);
  const [hasNewGeneralFeed, setHasNewGeneralFeed] = useState(false); // 일반/공지 새 글 여부
  const toggleSettingsSection = (section) => setSettingsCollapsed(prev => ({ ...prev, [section]: !prev[section] }));
  const expandAllSettings = () => setSettingsCollapsed({ basic: false, people: false, detail: false });
  const collapseAllSettings = () => setSettingsCollapsed({ basic: true, people: true, detail: true });
  /* eslint-enable no-unused-vars */

  const loadData = async (showLoader = true) => {
    // 이벤트 객체가 넘어올 수 있으므로 명시적으로 false가 아닐 때만 로딩 스피너 표시
    const shouldShow = showLoader !== false;
    try {
      if (shouldShow) setIsLoading(true);
      const [projRes, catRes, taskRes, instRes, commentsRes, feedRes] = await Promise.all([
        getProjectById(id, token),
        getCategories(id, token),
        getTasks(id, token),
        getProjectInstructions(id, token).catch(() => ({ ok: false })),
        getAllTaskCommentsByProject(id, token).catch(() => ({ ok: false })),
        getFeedbacks(id, token).catch(() => ({ ok: false }))
      ]);
      if (projRes.ok) setProject(projRes.data);
      if (catRes.ok) setCategories(catRes.data);
      if (taskRes.ok) {
        setTasks(taskRes.data);
      }
      if (instRes && instRes.ok) {
        const formatted = instRes.data.map(inst => {
          const dept = inst.user?.org?.dept_name ? ` [${inst.user.org.dept_name}]` : '';
          return {
            id: inst.id,
            author: `${inst.user?.name || 'Unknown'}${dept}`,
            content: inst.content,
            date: new Date(inst.created_at).toLocaleString()
          };
        });
        setInstructions(formatted);
      }
      if (commentsRes && commentsRes.ok) {
        setAllTaskComments(commentsRes.data);
      }
      if (feedRes && feedRes.ok) {
        // Feed Lounge에서 나를 향한 요청 중 해결되지 않은 것 카운트
        const uIdStr = String(user?.id);
        const uLoginIdStr = String(user?.userid);

        // --- NEW LOGIC: 역추적 방어 로직 ---
        const allMembers = projRes.data?.members || [];
        const myMemberRecord = allMembers.find(m => {
          const mUser = m.user || {};
          return String(mUser.userid) === uIdStr || String(mUser.userid) === uLoginIdStr ||
            String(m.user_id) === uIdStr || String(m.user_id) === uLoginIdStr ||
            String(mUser.id) === uIdStr || String(mUser.id) === uLoginIdStr;
        });
        const myNumericId = myMemberRecord ? String(myMemberRecord.user_id) : null;
        // -----------------------------------

        const unresolved = feedRes.data.filter(feed => {
          if (feed.type !== 'request' || feed.isResolved) return false;

          let targetIds = [];
          if (Array.isArray(feed.targetUserId)) {
            targetIds = feed.targetUserId;
          } else if (typeof feed.targetUserId === 'string') {
            try {
              targetIds = JSON.parse(feed.targetUserId);
              if (!Array.isArray(targetIds)) targetIds = [targetIds];
            } catch {
              targetIds = feed.targetUserId.split(',').map(s => s.trim());
            }
          } else if (feed.targetUserId) {
            targetIds = [feed.targetUserId];
          }

          const isAllTarget = targetIds.length === 0;
          const isExplicitlyTargeted = targetIds.map(String).some(id => id === uIdStr || id === uLoginIdStr || id === myNumericId);

          const feedAuthorId = String(feed.author?.id || feed.user_id || feed.author?.userid);
          const isAuthor = (feedAuthorId === uIdStr || feedAuthorId === uLoginIdStr || feedAuthorId === myNumericId);

          // 1. 내가 작성자이면 전체 대상이든 개별 지정이든 무조건 제외 (본인에게 알림 안 띄움)
          if (isAuthor) return false;
          // 2. 전체 대상도 아니고, 나를 명시적으로 지정한 것도 아니면 제외
          if (!isAllTarget && !isExplicitlyTargeted) return false;

          // 3. 위 조건을 통과한 요청 중, 내가 한 번이라도 답글(reply)을 달았는지 확인
          // (API 응답에서 parentId와 parent_id 혼용 가능성을 방지하기 위해 둘 다 체크하고, ID는 문자열로 변환하여 비교)
          const myReplies = feedRes.data.filter(c => {
            const replyAuthorId = String(c.author?.id || c.user_id || c.author?.userid);
            return (String(c.parentId) === String(feed.id) || String(c.parent_id) === String(feed.id)) &&
              (replyAuthorId === uIdStr || replyAuthorId === uLoginIdStr || replyAuthorId === myNumericId);
          });

          // 내 답글이 없을 때만(0개) 카운트에 포함
          return myReplies.length === 0;
        });
        setUnresolvedFeedCount(unresolved.length);

        // [일반/공지 새 글 판단 로직]
        let myLastVisitDate = null;
        if (projRes.data) {
          if (projRes.data.author_id === Number(uIdStr) || projRes.data.author?.userid === uLoginIdStr) {
            myLastVisitDate = projRes.data.author_last_visit_at ? new Date(projRes.data.author_last_visit_at) : null;
          } else if (projRes.data.reviewer_id === Number(uIdStr) || projRes.data.reviewer?.userid === uLoginIdStr) {
            myLastVisitDate = projRes.data.reviewer_last_visit_at ? new Date(projRes.data.reviewer_last_visit_at) : null;
          } else if (projRes.data.leader_id === Number(uIdStr) || projRes.data.leader?.userid === uLoginIdStr) {
            myLastVisitDate = projRes.data.leader_last_visit_at ? new Date(projRes.data.leader_last_visit_at) : null;
          } else {
            myLastVisitDate = myMemberRecord?.last_feed_visit_at ? new Date(myMemberRecord.last_feed_visit_at) : null;
          }
        }

        const lastVisitDate = myLastVisitDate;

        const hasNew = feedRes.data.some(feed => {
          if (feed.type === 'request') return false; // 요청은 제외

          // 내가 작성한 글이면 새 글 알림(깜빡임)에서 제외
          const fAuthorId = String(feed.author?.id || feed.user_id || feed.author?.userid);
          if (fAuthorId === uIdStr || fAuthorId === uLoginIdStr || fAuthorId === myNumericId) return false;

          if (!lastVisitDate) return true; // 접속 기록이 없으면 모두 새 글
          return new Date(feed.created_at || feed.createdAt) > lastVisitDate;
        });
        setHasNewGeneralFeed(hasNew);
      }
    } catch (err) {
      console.error('워크스페이스 데이터 로딩 실패:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // 사용자 정보가 완전히 로딩된 후에만 API 호출 (일시적 null 상태로 인한 오작동 원천 차단)
    if (id && token && user && (user.id || user.userid)) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token, user?.id, user?.userid]);

  // 외부 링크나 To-Do 등에서 파라미터(tab=comments)를 통해 탭으로 바로 진입한 경우 자동 업데이트
  useEffect(() => {
    if (activeTab === 'comments' && hasNewGeneralFeed) {
      updateProjectVisit(id, token)
        .then(() => setHasNewGeneralFeed(false))
        .catch(console.error);
    }
  }, [activeTab, hasNewGeneralFeed, id, token]);

  const toggleDrawer = (task = null, tab = 'checklist', focusId = null) => {
    setSelectedTask(task);
    setDrawerTab(tab);
    setDrawerFocusId(focusId);
    setIsDrawerOpen(!!task);
  };

  // 드로어 열림 상태에 따라 포탈(body) 스크롤 잠금 처리
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    // 컴포넌트 언마운트 시 스크롤 복구
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isDrawerOpen]);

  // --- 외부(To-Do 등)에서 파라미터로 접근 시 자동 서랍 열기 ---
  const processedUrlRef = useRef('');

  useEffect(() => {
    if (tasks && tasks.length > 0) {
      if (processedUrlRef.current === location.search) return;

      const searchParams = new URLSearchParams(location.search);
      const urlTaskId = searchParams.get('taskId');
      const urlTab = searchParams.get('tab');
      const urlFocusId = searchParams.get('focusId');

      if (urlTaskId && urlTaskId !== 'undefined' && urlTaskId !== 'null') {
        let targetTask = null;
        if (categories && categories.length > 0) {
          for (const cat of categories) {
            targetTask = cat.tasks?.find(t => String(t.id) === urlTaskId);
            if (targetTask) break;
          }
        }
        if (!targetTask) {
          targetTask = tasks.find(t => String(t.id) === urlTaskId);
        }
        if (targetTask) {
          processedUrlRef.current = location.search;
          toggleDrawer(targetTask, urlTab || 'checklist', urlFocusId);
        }
      } else if (urlTab) {
        processedUrlRef.current = location.search;
        const normalizedTab = (urlTab === 'feedback' || urlTab === 'feed') ? 'comments' : urlTab;
        setActiveTab(normalizedTab);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, categories, location.search]);

  const handleAddCategory = () => {
    setAddCategoryModal({ isOpen: true, name: '', sortOrder: categories.length + 1 });
  };

  const handleSaveNewCategory = async () => {
    const name = addCategoryModal.name.trim();
    if (!name) {
      alert('분류명이 입력되지 않았습니다.');
      return;
    }
    try {
      const res = await createCategory(id, { name, sort_order: addCategoryModal.sortOrder }, token);
      if (res.ok) {
        setCategories([...categories, res.data]);
        setAddCategoryModal({ isOpen: false, name: '', sortOrder: 10 });
      }
    } catch (err) {
      alert('분류 생성에 실패했습니다.');
    }
  };

  const handleSaveCategoryEdit = async () => {
    if (!editCategoryModal.name.trim()) {
      alert('분류명이 입력되지 않았습니다.');
      return;
    }
    try {
      const res = await updateCategory(editCategoryModal.categoryId, { name: editCategoryModal.name.trim(), sort_order: editCategoryModal.sortOrder }, token);
      if (res.ok) {
        setCategories(categories.map(c => c.id === editCategoryModal.categoryId ? { ...c, name: editCategoryModal.name.trim(), sort_order: editCategoryModal.sortOrder } : c));
        setEditCategoryModal({ isOpen: false, categoryId: null, name: '', sortOrder: 10 });
      }
    } catch (err) {
      alert('분류 수정에 실패했습니다.');
    }
  };

  const handleDeleteCategory = async () => {
    if (!window.confirm('정말 이 분류를 삭제하시겠습니까? (하위 세부 업무가 없어야 합니다)')) return;
    try {
      const res = await deleteCategory(editCategoryModal.categoryId, token);
      if (res.ok) {
        setCategories(categories.filter(c => c.id !== editCategoryModal.categoryId));
        setEditCategoryModal({ isOpen: false, categoryId: null, name: '', sortOrder: 10 });
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        alert(err.response.data.message);
      } else {
        alert('분류 삭제에 실패했습니다.');
      }
    }
  };

  const openTaskModal = (categoryId) => {
    setSelectedCategoryId(categoryId);
    setShowTaskModal(true);
  };

  const handleTaskSubmit = async (taskData) => {
    try {
      const res = await createTask(id, taskData, token);
      if (res.ok) {
        setTasks([...tasks, res.data]);
        setShowTaskModal(false);
      }
    } catch (err) {
      alert('업무 등록에 실패했습니다.');
    }
  };

  const handleReorderTasks = async (sourceCategoryId, draggedTaskId, targetTaskId, targetCategoryId = sourceCategoryId, position = 'before') => {
    // 1. 동일 분류 내 순서 변경
    if (String(sourceCategoryId) === String(targetCategoryId)) {
      const categoryTasks = tasks
        .filter(t => String(t.category_id) === String(sourceCategoryId))
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

      const draggedIndex = categoryTasks.findIndex(t => t.id === draggedTaskId);
      if (draggedIndex === -1) return;

      const newCategoryTasks = [...categoryTasks];
      const [draggedItem] = newCategoryTasks.splice(draggedIndex, 1);

      let insertIndex = newCategoryTasks.length;
      if (targetTaskId) {
        const targetIndex = newCategoryTasks.findIndex(t => t.id === targetTaskId);
        if (targetIndex !== -1) {
          insertIndex = position === 'after' ? targetIndex + 1 : targetIndex;
        }
      }

      newCategoryTasks.splice(insertIndex, 0, draggedItem);

      const taskOrders = newCategoryTasks.map((t, index) => ({
        id: t.id,
        sort_order: index + 1
      }));

      setTasks(prevTasks => prevTasks.map(t => {
        const orderInfo = taskOrders.find(o => o.id === t.id);
        return orderInfo ? { ...t, sort_order: orderInfo.sort_order } : t;
      }));

      try {
        await reorderTasks(sourceCategoryId, taskOrders, token);
      } catch (e) {
        alert('순서 저장에 실패했습니다.');
        loadData();
      }
      return;
    }

    // 2. 다른 분류로의 이동
    const draggedTask = tasks.find(t => t.id === draggedTaskId);
    if (!draggedTask) return;

    // 출발 분류 목록 (draggedTask 제외 후 1부터 재부여)
    const sourceCategoryTasks = tasks
      .filter(t => String(t.category_id) === String(sourceCategoryId) && t.id !== draggedTaskId)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

    const sourceTaskOrders = sourceCategoryTasks.map((t, index) => ({
      id: t.id,
      sort_order: index + 1
    }));

    // 도착 분류 목록 (targetTaskId 위치에 draggedTask 삽입)
    const targetCategoryTasks = tasks
      .filter(t => String(t.category_id) === String(targetCategoryId) && t.id !== draggedTaskId)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

    let insertIndex = targetCategoryTasks.length; // 기본값: 맨 뒤
    if (targetTaskId) {
      const targetIdx = targetCategoryTasks.findIndex(t => t.id === targetTaskId);
      if (targetIdx !== -1) {
        insertIndex = position === 'after' ? targetIdx + 1 : targetIdx;
      }
    }

    const updatedDraggedTask = { ...draggedTask, category_id: targetCategoryId };
    targetCategoryTasks.splice(insertIndex, 0, updatedDraggedTask);

    const targetTaskOrders = targetCategoryTasks.map((t, index) => ({
      id: t.id,
      sort_order: index + 1
    }));

    // 로컬 React state 즉각 반영 (지연 없이 즉시 UI 이동)
    setTasks(prevTasks => prevTasks.map(t => {
      if (t.id === draggedTaskId) {
        const orderInfo = targetTaskOrders.find(o => o.id === t.id);
        return { ...t, category_id: targetCategoryId, sort_order: orderInfo ? orderInfo.sort_order : 1 };
      }
      const sOrder = sourceTaskOrders.find(o => o.id === t.id);
      if (sOrder) return { ...t, sort_order: sOrder.sort_order };
      const tOrder = targetTaskOrders.find(o => o.id === t.id);
      if (tOrder) return { ...t, sort_order: tOrder.sort_order };
      return t;
    }));

    try {
      // 1. 소속 카테고리 변경 API 실행
      await updateTaskCategory(draggedTaskId, targetCategoryId, token);

      // 2. 출발/도착 카테고리 순서 동기화
      await Promise.all([
        sourceTaskOrders.length > 0 ? reorderTasks(sourceCategoryId, sourceTaskOrders, token) : Promise.resolve(),
        reorderTasks(targetCategoryId, targetTaskOrders, token)
      ]);
    } catch (e) {
      console.error('타 분류 업무 이동 실패:', e);
      alert('업무 분류 이동에 실패했습니다.');
      loadData();
    }
  };

  const handleReorderCategories = async (draggedId, targetId) => {
    const draggedIndex = categories.findIndex(c => c.id === draggedId);
    const targetIndex = categories.findIndex(c => c.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // 1. 삽입 방식으로 배열 재구성
    const newCategories = [...categories];
    const [draggedItem] = newCategories.splice(draggedIndex, 1);
    newCategories.splice(targetIndex, 0, draggedItem);

    // 2. 새 sort_order 할당 (1부터 순서대로)
    const updatedCategories = newCategories.map((c, index) => ({
      ...c,
      sort_order: index + 1
    }));

    // 3. 화면 즉각 반영 (낙관적 업데이트)
    setCategories(updatedCategories);

    try {
      // 4. 순서가 변경된 항목들만 추려내서 서버에 동시 저장
      const updates = updatedCategories.filter(uc => {
        const original = categories.find(c => c.id === uc.id);
        return original.sort_order !== uc.sort_order;
      });

      if (updates.length > 0) {
        await Promise.all(
          updates.map(uc => updateCategory(uc.id, { name: uc.name, sort_order: uc.sort_order }, token))
        );
      }
    } catch (err) {
      console.error('분류 순서 변경 실패:', err);
      alert('분류 순서를 변경하는 데 실패했습니다.');
      loadData(); // 원복
    }
  };

  const fundamentallySortedTasks = React.useMemo(() => {
    if (!categories || !tasks || !project || !user) return [];

    const uId = String(user.id);
    const uLoginId = user.userid ? String(user.userid) : uId;

    // 1. 프로젝트 마스터 판별
    const isProjectMaster = (() => {
      if (user.role === 'admin') return true;
      return String(project.author_id) === uId || String(project.author_id) === uLoginId ||
        String(project.leader_id) === uId || String(project.leader_id) === uLoginId ||
        String(project.reviewer_id) === uId || String(project.reviewer_id) === uLoginId;
    })();

    // 2. 현재 로그인한 사람이 "프로젝트의 일반 담당자"인지 판별 (참조자가 아님)
    const isProjectMember = project.members && project.members.some(m =>
      String(m.user_id) === uId ||
      String(m.user_id) === uLoginId ||
      (m.user && String(m.user.id) === uId) ||
      (m.user && String(m.user.userid) === uLoginId)
    );

    // 3. 기획 의도에 맞춘 완벽한 필터링 복구
    const visibleTasks = tasks.filter(t => {
      if (isProjectMaster) return true; // 마스터는 다 봄
      if (isProjectMember) return true; // 프로젝트 일반 담당자도 트렌드에 따라 다 봄

      // ★ 프로젝트 참조자는 본인이 세부항목에 명시적으로 지정된 것만 봄 ★
      return t.members && t.members.some(m =>
        String(m.user_id) === uId ||
        String(m.user_id) === uLoginId ||
        (m.user && String(m.user.id) === uId) ||
        (m.user && String(m.user.userid) === uLoginId)
      );
    });

    // 1. 카테고리를 sort_order 기준으로 오름차순 정렬
    const sortedCats = [...categories].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

    let result = [];
    // 2. 정렬된 카테고리 순서대로 세부항목들을 가져와서 묶음 (세부항목 내에서도 sort_order 순으로 정렬)
    sortedCats.forEach(cat => {
      const catTasks = visibleTasks
        .filter(t => String(t.category_id) === String(cat.id))
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      result = result.concat(catTasks);
    });

    // 3. (안전망) 혹시 카테고리가 삭제되었거나 누락된 고아(orphan) 세부항목이 있다면 맨 뒤에 붙임
    const unassigned = visibleTasks.filter(t => !categories.find(c => String(c.id) === String(t.category_id)));
    return result.concat(unassigned);
  }, [categories, tasks, project, user]);

  const getGanttTasks = () => {
    const validTasks = fundamentallySortedTasks.filter(t => t.start_date && t.end_date).map(t => ({
      start: new Date(t.start_date),
      end: new Date(t.end_date),
      name: t.title,
      id: `Task_${t.id}`,
      type: 'task',
      progress: t.progress_rate || 0,
      isDisabled: false,
      styles: { progressColor: t.status === 'completed' ? '#198754' : '#0d6efd', progressSelectedColor: '#0a58ca' },
      project: `Category_${t.category_id}`
    }));
    const validCategoryIds = new Set(validTasks.map(t => t.project));
    const projectTasks = categories.filter(c => validCategoryIds.has(`Category_${c.id}`)).map(c => {
      const cTasks = validTasks.filter(t => t.project === `Category_${c.id}`);
      return {
        start: new Date(Math.min(...cTasks.map(t => t.start.getTime()))),
        end: new Date(Math.max(...cTasks.map(t => t.end.getTime()))),
        name: c.name,
        id: `Category_${c.id}`,
        type: 'project',
        progress: 0,
        hideChildren: false
      };
    });
    const finalTasks = [...projectTasks, ...validTasks];
    if (finalTasks.length === 0) {
      return [{ start: new Date(), end: new Date(), name: '일정이 입력된 업무가 없습니다.', id: 'dummy', type: 'task', progress: 0, isDisabled: true }];
    }
    return finalTasks;
  };

  // eslint-disable-next-line no-unused-vars
  const getCalendarEvents = () => {
    return fundamentallySortedTasks.filter(t => t.start_date).map((t, index) => {
      const member = calendarMembers[index % calendarMembers.length];
      return {
        id: String(t.id),
        title: `[${t.progress_rate}%] ${t.title}`,
        start: t.start_date,
        end: t.end_date ? new Date(new Date(t.end_date).getTime() + 86400000).toISOString().split('T')[0] : t.start_date,
        backgroundColor: member.checked ? member.color : 'transparent',
        borderColor: member.checked ? member.color : 'transparent',
        textColor: member.checked ? '#fff' : 'transparent',
        extendedProps: { task: t }
      };
    });
  };

  const calculatedProgress = React.useMemo(() => {
    if (!tasks || tasks.length === 0) return project?.progress_rate || 0;
    const totalProgress = tasks.reduce((sum, t) => sum + (Number(t.progress_rate) || 0), 0);
    return Math.round(totalProgress / tasks.length);
  }, [tasks, project]);

  if (isLoading) return <div className="p-5 text-center"><div className="spinner-border text-primary"></div></div>;
  if (!project) return <div className="p-5 text-center text-muted">프로젝트를 찾을 수 없습니다.</div>;

  // eslint-disable-next-line no-unused-vars
  const isCreator = true; // 작성자 권한 더미 처리 (추후 연동)

  const isCompleted = project.status === 'completed';
  const todayStr = new Date().toISOString().split('T')[0];
  const isDelayed = !isCompleted && project.end_date && project.end_date < todayStr;

  let headerBadgeText = '진행중';
  let headerBadgeClass = 'bg-primary';

  if (isCompleted) {
    headerBadgeText = '완료';
    headerBadgeClass = 'bg-success';
  } else if (isDelayed) {
    headerBadgeText = (project.status === 'ready' || project.status === 'pending') ? '시작 지연' : '지연';
    headerBadgeClass = 'bg-danger';
  } else if (project.status === 'ready') {
    headerBadgeText = '시작 전';
    headerBadgeClass = 'bg-warning text-dark';
  } else if (project.status === 'pending') {
    headerBadgeText = '대기중';
    headerBadgeClass = 'bg-warning text-dark';
  } else if (project.status === 'review') {
    headerBadgeText = '검토중';
    headerBadgeClass = 'bg-info text-dark';
  } else if (project.status === 'rejected') {
    headerBadgeText = '반려됨';
    headerBadgeClass = 'bg-danger text-white';
  } else if (project.status === 'canceled') {
    headerBadgeText = '취소됨';
    headerBadgeClass = 'bg-secondary';
  }

  return (
    <div className="container-fluid p-0 d-flex flex-column h-100 rounded-4" style={{ overflowX: 'hidden', backgroundColor: '#f4f5f7', border: '1px solid #ced4da' }}>

      {/* 🚀 상단 타이틀 독립 카드 (부드러운 배경) */}
      <div className="pt-4 px-4 z-1">
        <div className="rounded-4 p-4 d-flex justify-content-between align-items-center shadow-sm" style={{ backgroundColor: '#ffffff', border: '1px solid #ced4da' }}>
          <div className="d-flex align-items-center">
            <button className="btn btn-white shadow-sm me-4 fw-bold rounded-circle d-flex justify-content-center align-items-center" style={{ width: '40px', height: '40px', border: '1px solid #dee2e6' }} onClick={() => navigate('/pms/project-rooms')}>
              <i className="fas fa-arrow-left text-secondary"></i>
            </button>
            <div>
              <h5 className="mb-2 fw-bolder d-flex align-items-center text-dark" style={{ fontSize: '1.25rem' }}>
                <i className="fas fa-project-diagram text-primary me-2"></i>
                <span
                  onClick={() => navigate(`/pms/projects/${id}`)}
                  style={{ cursor: 'pointer', textDecoration: 'none' }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  {project.title}
                </span>
                <span className={`badge ${headerBadgeClass} ms-3 fw-normal px-2 py-1`} style={{ fontSize: '0.75rem' }}>{headerBadgeText}</span>
              </h5>
              <div className="text-muted fw-bold d-flex align-items-center" style={{ fontSize: '0.85rem' }}>
                <i className="far fa-calendar-alt me-1"></i>기간: {project.start_date} ~ {project.end_date}
                <span className="mx-3 text-light">|</span>
                <i className="fas fa-chart-line me-1"></i>진척도: {calculatedProgress}%
                <div className="progress ms-2 shadow-sm" style={{ width: '200px', height: '8px', borderRadius: '4px', backgroundColor: '#ced4da' }}>
                  <div className="progress-bar bg-primary" style={{ width: `${calculatedProgress}%`, borderRadius: '4px' }}></div>
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center">
            <style>{`
              .refresh-btn:hover .fa-sync-alt { transform: rotate(180deg); }
              .refresh-btn .fa-sync-alt { transition: transform 0.3s ease-in-out; }
            `}</style>
            <button className="btn btn-light fw-bold text-dark btn-sm shadow-sm border px-3 py-1 refresh-btn" onClick={loadData}>
              <i className="fas fa-sync-alt me-1"></i>새로고침
            </button>
          </div>
        </div>
      </div>

      {/* 6대 탑 탭 (Top Tabs) - IDE/브라우저 스타일 탭 (본문과 연결) */}
      <div className="px-4 mt-3 z-1">
        <div className="d-flex align-items-end justify-content-between" style={{ borderBottom: '1px solid #dee2e6' }}>
          <div className="d-flex align-items-end">
            {[
              { id: 'home', icon: 'fas fa-columns', label: 'HOME' },
              { id: 'timeline', icon: 'fas fa-stream', label: '타임라인' },
              { id: 'calendar', icon: 'fas fa-clipboard-list', label: '진행현황' },
              { id: 'files', icon: 'fas fa-folder-open', label: '파일' },
              { id: 'comments', icon: 'far fa-comments', label: '코멘트&피드', count: unresolvedFeedCount },
              { id: 'settings', icon: 'fas fa-cog', label: '프로젝트설정' },
              { id: 'bo', icon: 'fas fa-file-invoice-dollar', label: 'Business Order' },
            ].map(tab => (
              <div key={tab.id}
                className={`px-4 py-2 d-flex align-items-center ${activeTab === tab.id ? 'fw-bold text-primary' : 'fw-semibold text-dark'}`}
                style={{
                  cursor: 'pointer',
                  backgroundColor: activeTab === tab.id ? '#f4f5f7' : '#e9ecef',
                  borderTop: activeTab === tab.id ? '3px solid #0d6efd' : '1px solid #dee2e6',
                  borderLeft: '1px solid #dee2e6',
                  borderRight: '1px solid #dee2e6',
                  borderBottom: activeTab === tab.id ? '1px solid #f4f5f7' : '1px solid #dee2e6',
                  borderTopLeftRadius: '6px',
                  borderTopRightRadius: '6px',
                  marginBottom: '-1px', // 컨테이너의 bottom border 덮기
                  fontSize: '0.9rem',
                  minWidth: '120px',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => { if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = '#d3d9df'; }}
                onMouseLeave={(e) => { if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = '#e9ecef'; }}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'comments' && hasNewGeneralFeed) {
                    updateProjectVisit(id, token).then(() => setHasNewGeneralFeed(false)).catch(console.error);
                  }
                  // 탭을 수동으로 클릭할 때는 이전 URL에 남아있던 파라미터(focusId 등)를 제거하여 불필요한 자동 스크롤 방지
                  if (location.search) {
                    navigate(location.pathname, { replace: true, state: {} });
                  }
                }}>
                <style>{`
                  @keyframes pulse-border {
                    0% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7); }
                    70% { box-shadow: 0 0 0 6px rgba(220, 53, 69, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); }
                  }
                  .pulse-badge {
                    animation: pulse-border 1.5s infinite;
                  }
                `}</style>
                <i className={`${tab.icon} me-2`}></i>{tab.label}
                {tab.id === 'comments' ? (
                  tab.count > 0 ? (
                    <span
                      className={`badge ms-2 d-flex align-items-center justify-content-center ${hasNewGeneralFeed ? 'pulse-badge' : ''}`}
                      style={{
                        fontSize: '0.75rem',
                        minWidth: '20px',
                        height: '20px',
                        borderRadius: '10px',
                        backgroundColor: '#dc3545',
                        border: hasNewGeneralFeed ? '2px solid #ffc107' : 'none',
                        padding: '0 6px',
                        margin: 0
                      }}
                    >
                      {tab.count}
                    </span>
                  ) : hasNewGeneralFeed ? (
                    <span
                      className="badge ms-2 d-flex align-items-center justify-content-center"
                      style={{
                        fontSize: '0.75rem',
                        minWidth: '20px',
                        height: '20px',
                        borderRadius: '10px',
                        backgroundColor: '#40c057',
                        padding: '0 6px',
                        margin: 0
                      }}
                    >
                      N
                    </span>
                  ) : null
                ) : (
                  tab.count > 0 && (
                    <span
                      className="badge ms-2 d-flex align-items-center justify-content-center bg-danger"
                      style={{
                        fontSize: '0.75rem',
                        minWidth: '20px',
                        height: '20px',
                        borderRadius: '10px',
                        padding: '0 6px',
                        margin: 0
                      }}
                    >
                      {tab.count}
                    </span>
                  )
                )}
              </div>
            ))}
          </div>

          {/* 우측 칸반 접기/펼치기 제어 버튼 (HOME 탭 + 분류 존재 시에만 노출) */}
          {activeTab === 'home' && categories.length > 0 && (
            <div className="pb-2">
              <button className="btn btn-sm btn-outline-primary shadow-sm me-2 fw-bold" onClick={expandAllPhases}><i className="fas fa-angle-double-down me-1"></i>전체 펼치기</button>
              <button className="btn btn-sm btn-outline-primary shadow-sm fw-bold" onClick={collapseAllPhases}><i className="fas fa-angle-double-up me-1"></i>전체 접기</button>
            </div>
          )}
        </div>
      </div>

      {/* 🧩 메인 워크스페이스 영역 */}
      <div className="d-flex flex-grow-1 position-relative" style={{ backgroundColor: '#f4f5f7' }}>

        <div id="pms-main-scroll" className="flex-grow-1 px-4 pb-4 pt-3 overflow-auto" style={{ transition: 'margin-right 0.3s' }}>

          {/* ======================= [1. HOME (Phase & Task 칸반)] ======================= */}
          {activeTab === 'home' && (
            <HomeTab
              categories={categories}
              tasks={fundamentallySortedTasks}
              collapsedPhases={collapsedPhases}
              setCollapsedPhases={setCollapsedPhases}
              handleAddCategory={handleAddCategory}
              setEditCategoryModal={setEditCategoryModal}
              openTaskModal={openTaskModal}
              toggleDrawer={toggleDrawer}
              onReorderTasks={handleReorderTasks}
              onReorderCategories={handleReorderCategories}
              isProjectMaster={isProjectMaster}
              canCreateTask={canCreateTask}
            />
          )}

          {/* ======================= [2. 타임라인 (Gantt)] ======================= */}
          {activeTab === 'timeline' && (
            <TimelineTab getGanttTasks={getGanttTasks} toggleDrawer={toggleDrawer} tasks={fundamentallySortedTasks} categories={categories} project={project} onDataRefresh={loadData} isProjectMaster={isProjectMaster} currentUser={user} />
          )}

          {/* ======================= [3. 진행현황 (Kanban Board)] ======================= */}
          {activeTab === 'calendar' && (
            <BoardTab toggleDrawer={toggleDrawer} tasks={fundamentallySortedTasks} setTasks={setTasks} categories={categories} isProjectMaster={isProjectMaster} canCreateTask={canCreateTask} currentUser={user} project={project} setProject={setProject} onDataRefresh={loadData} />
          )}

          {/* ======================= [4. 파일 (마스터 전역 뷰)] ======================= */}
          {activeTab === 'files' && (
            <FilesTab tasks={fundamentallySortedTasks} categories={categories} onOpenTask={toggleDrawer} isProjectMaster={isProjectMaster} canCreateTask={canCreateTask} refreshTaskSignal={refreshTaskSignal} />
          )}

          {/* ======================= [5. 코멘트&지시사항 (Split 듀얼 뷰)] ======================= */}
          {activeTab === 'comments' && (
            <CommentsTab
              tasks={fundamentallySortedTasks}
              categories={categories}
              allTaskComments={allTaskComments}
              instructions={instructions}
              newInstruction={newInstruction}
              setNewInstruction={setNewInstruction}
              handleCreateInstruction={handleCreateInstruction}
              isProjectMaster={isProjectMaster}
              toggleDrawer={toggleDrawer}
              canCreateTask={canCreateTask}
              project={project}
              currentUser={user}
              onDataRefresh={() => loadData(false)}
            />
          )}

          {/* ======================= [6. 프로젝트 설정] ======================= */}
          {activeTab === 'settings' && (
            <div className="bg-white rounded shadow-sm border h-100 overflow-auto animation-fade-in">
              {isEditingSettings ? (
                <ProjectForm
                  isInline={true}
                  onCancelInline={() => setIsEditingSettings(false)}
                  onSuccessInline={() => {
                    setIsEditingSettings(false);
                    loadData(); // 정보 업데이트
                  }}
                  calculatedProgress={calculatedProgress}
                />
              ) : (
                <ProjectView
                  isInline={true}
                  onEditInline={() => setIsEditingSettings(true)}
                  calculatedProgress={calculatedProgress}
                  onDataRefresh={() => loadData(false)}
                  tasksCount={tasks.length}
                />
              )}
            </div>
          )}

          {/* ======================= [7. 매출품의서(BO)] ======================= */}
          {activeTab === 'bo' && (
            <div className="h-100">
              <BusinessOrderTab project={project} tasks={fundamentallySortedTasks} />
            </div>
          )}

        </div>

        {/* 💡 우측 슬라이드 드로어 (Drawer) 패널 및 전체 화면 백드롭 - Portal */}
        {ReactDOM.createPortal(
          <>
            {isDrawerOpen && (
              <div className="position-fixed top-0 start-0 w-100 h-100" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9997 }} onClick={() => toggleDrawer(null)}></div>
            )}
            <div className="bg-white border-start shadow-lg position-fixed end-0 d-flex flex-column"
              style={{
                width: '50vw',
                height: '100vh',
                top: 0,
                right: 0,
                transform: isDrawerOpen ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                zIndex: 9998
              }}>
              {selectedTask && <TaskDrawerPanel task={selectedTask} categoryName={categories.find(c => String(c.id) === String(selectedTask.category_id))?.name || ''} project={project} defaultTab={drawerTab} focusId={drawerFocusId} onClose={() => toggleDrawer(null)} onTaskDataChanged={refreshTaskData} onTaskUpdate={(updatedTask) => {
                // 세부항목 리스트(카드) 실시간 갱신
                setTasks(prev => prev.map(t => t.id === updatedTask.id ? updatedTask : t));
                // 현재 열린 드로어의 task도 최신으로 갱신
                setSelectedTask(updatedTask);
              }} onTaskDelete={(taskId) => {
                setTasks(prev => prev.filter(t => t.id !== taskId));
                toggleDrawer(null);
              }} />}
            </div>
          </>,
          document.body
        )}

      </div>

      <TaskCreateModal show={showTaskModal} onHide={() => setShowTaskModal(false)} onSubmit={handleTaskSubmit} categoryId={selectedCategoryId} categoryName={categories.find(c => c.id === selectedCategoryId)?.name || ''} project={project} />

      {/* 💡 분류 수정 레이어(모달) 팝업 (전체 화면 백드롭) */}
      {editCategoryModal.isOpen && ReactDOM.createPortal(
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center animation-fade-in" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="bg-white rounded-3 shadow-lg d-flex flex-column" style={{ width: '450px', maxWidth: '90%', overflow: 'hidden' }}>

            {/* Header */}
            <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center" style={{ backgroundColor: '#f8f9fa' }}>
              <h5 className="fw-bold mb-0 text-dark">
                <i className="fas fa-edit text-primary me-2"></i>분류명 수정
              </h5>
              <button className="btn-close" onClick={() => setEditCategoryModal({ isOpen: false, categoryId: null, name: '' })}></button>
            </div>

            {/* Body */}
            <div className="p-4 bg-white">
              <label className="form-label fw-bold text-secondary mb-2">변경할 분류명을 입력하세요.</label>
              <div className="input-group input-group-lg shadow-sm rounded mb-3">
                <input
                  type="text"
                  className="form-control fs-6"
                  placeholder="예: Phase 1: 요구사항 기획"
                  value={editCategoryModal.name}
                  onChange={(e) => setEditCategoryModal(prev => ({ ...prev, name: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveCategoryEdit()}
                  autoFocus
                />
              </div>
              <label className="form-label fw-bold text-secondary mb-2">정렬 순서 (sort_order)</label>
              <div className="input-group shadow-sm rounded">
                <input
                  type="number"
                  className="form-control fs-6"
                  placeholder="예: 1, 2, 3 ..."
                  min="0"
                  value={editCategoryModal.sortOrder}
                  onChange={(e) => setEditCategoryModal(prev => ({ ...prev, sortOrder: Number(e.target.value) }))}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-top d-flex justify-content-between gap-2" style={{ backgroundColor: '#f8f9fa' }}>
              <div>
                {tasks.filter(t => t.category_id === editCategoryModal.categoryId).length === 0 && (
                  <button className="btn btn-outline-danger px-3 fw-bold shadow-sm" onClick={handleDeleteCategory}>
                    <i className="fas fa-trash-alt me-1"></i>삭제
                  </button>
                )}
              </div>
              <div className="d-flex gap-2">
                <button className="btn btn-secondary px-3 fw-bold shadow-sm" onClick={() => setEditCategoryModal({ isOpen: false, categoryId: null, name: '' })}>
                  취소
                </button>
                <button className="btn btn-primary px-3 fw-bold shadow-sm" onClick={handleSaveCategoryEdit}>
                  <i className="fas fa-check me-1"></i>저장
                </button>
              </div>
            </div>

          </div>
        </div>,
        document.body
      )}

      {/* 💡 새로운 분류 추가 레이어(모달) 팝업 (전체 화면 백드롭) */}
      {addCategoryModal.isOpen && ReactDOM.createPortal(
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center animation-fade-in" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="bg-white rounded-3 shadow-lg d-flex flex-column" style={{ width: '450px', maxWidth: '90%', overflow: 'hidden' }}>

            {/* Header */}
            <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center" style={{ backgroundColor: '#f8f9fa' }}>
              <h5 className="fw-bold mb-0 text-dark">
                <i className="fas fa-edit text-primary me-2"></i>분류명 추가
              </h5>
              <button className="btn-close" onClick={() => setAddCategoryModal({ isOpen: false, name: '' })}></button>
            </div>

            {/* Body */}
            <div className="p-4 bg-white">
              <label className="form-label fw-bold text-secondary mb-2">새로운 분류명을 입력하세요.</label>
              <div className="input-group input-group-lg shadow-sm rounded mb-3">
                <input
                  type="text"
                  className="form-control fs-6"
                  placeholder="예: Phase 1: 요구사항 기획"
                  value={addCategoryModal.name}
                  onChange={(e) => setAddCategoryModal(prev => ({ ...prev, name: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveNewCategory()}
                  autoFocus
                />
              </div>
              <label className="form-label fw-bold text-secondary mb-2">정렬 순서 (sort_order)</label>
              <div className="input-group shadow-sm rounded">
                <input
                  type="number"
                  className="form-control fs-6"
                  placeholder="예: 1, 2, 3 ..."
                  min="0"
                  value={addCategoryModal.sortOrder}
                  onChange={(e) => setAddCategoryModal(prev => ({ ...prev, sortOrder: Number(e.target.value) }))}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-top d-flex justify-content-end gap-2" style={{ backgroundColor: '#f8f9fa' }}>
              <button className="btn btn-secondary px-3 fw-bold shadow-sm" onClick={() => setAddCategoryModal({ isOpen: false, name: '' })}>
                취소
              </button>
              <button className="btn btn-primary px-3 fw-bold shadow-sm" onClick={handleSaveNewCategory}>
                <i className="fas fa-check me-1"></i>저장
              </button>
            </div>

          </div>
        </div>,
        document.body
      )}

    </div>
  );
}

export default ProjectWorkspace;
